"use strict";

import BaseModel from "@/shared/lib/client-sdk/base-model";
import ApiRequest from "@/shared/lib/client-sdk/api-request";
import $ from "jquery";
import OrderLine from "@/shared/lib/client-sdk/models/order-line";
import Plan from "@/shared/lib/client-sdk/models/plan";
import Service from "@/shared/lib/client-sdk/models/service";
import Account from "@/shared/lib/client-sdk/models/account";
import User from "@/shared/lib/client-sdk/models/user";

class Order extends BaseModel {
  constructor(idOrAttributes, onRefreshCallback = null) {
    super(idOrAttributes, onRefreshCallback);

    this._isFetchingAssociateableOrderLines = false;
    this._associateableOrderLines = [];

    // TECH-8849: staged (not-yet-uploaded) supplemental File. It's a client-only
    // holder that never round-trips through the API, so it isn't seeded via
    // set()/Vue.set() like DB-backed fields. Initialize it here so Vue observes
    // it as reactive from construction — otherwise computeds that depend on it
    // (isGenericAwaitingFile, isSupplementDocumentFormatInvalid) never recompute.
    this.supplementDocument = null;
  }

  static getModelClassName() {
    return "Order";
  }

  static getApiPathForSearch() {
    return (
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") + "/public/orders"
    );
  }

  getApiPathForCreate() {
    return (
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/accounts/" +
      this.accountId +
      "/orders"
    );
  }

  getApiPathForRefreshUpdateAndDelete() {
    return (
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/orders/" +
      this.id
    );
  }

  getAdminPath() {
    if (this.isBulkOrder && this.isEnhancedIraService && this.hasOrderUploads) {
      return `/accounts/${this.masterAccountId}/bulk-orders/${this.id}#bulk-operations`;
    }

    return "/orders/" + this.id;
  }

  getClientPath() {
    return "/" + this.masterAccountId + "/orders/" + this.id;
  }

  get relationships() {
    return {
      plan: Plan,
      service: Service,
      subaccount: Account,
      masterAccount: Account,
      createdByUser: User,
      submittedByUser: User,
      approvedByUser: User,
    };
  }

  touch(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/orders/" +
      this.id +
      "/touch";

    ApiRequest.send("PATCH", requestUrl).onComplete(
      function (response) {
        if (response.successful) {
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  get searchResultTitle() {
    if (this.friendlyId.length >= 15) {
      return (
        this.friendlyId.substr(0, 2) +
        "-" +
        this.friendlyId.substr(2, 6) +
        "-" +
        this.friendlyId.substr(8, 3) +
        "-" +
        this.friendlyId.substr(11)
      );
    } else {
      return this.friendlyId;
    }
  }

  // Returns a string, or an array of strings (for a multi-line)
  get searchResultSubtitle() {
    return [
      this.serviceDisplayName,
      Number.withCommas(this.totalOrderLines) + " participants",
    ];
  }

  get searchResultCreatedAt() {
    return this.createdAt;
  }

  get searchResultStatus() {
    return this.status;
  }

  get requiresDistributionServiceDetail() {
    return (
      this.isDistributionsService ||
      this.isRolloverService ||
      this.isUncashedChecksService ||
      // Prospect / party-crasher application rollovers moved to the nextLevelContribution
      // service code (TECH-12322) but still use the legacy distribution detail model.
      // Additional Contributions share that service code and are excluded — they use
      // contributionToNextLevelIra* payment instructions, not rollover ones.
      this.hasNextLevelIraRolloverPaymentInstructions
    );
  }

  get requiresTaxServiceDetail() {
    return this.isTaxService;
  }

  get orderLineName() {
    if (this.isVendorDistributionsService) return "payee";

    return "participant";
  }

  get isTaxService() {
    return (
      this.serviceCode == "taxFormsOnly" ||
      this.serviceCode == "taxPaymentsAndForms"
    );
  }

  get isTaxFormsOnlyService() {
    return this.serviceCode == "taxFormsOnly";
  }

  get isTaxPaymentsAndFormsService() {
    return this.serviceCode == "taxPaymentsAndForms";
  }

  get isDistributionsService() {
    return (
      this.serviceCode == "premierDistributions" ||
      this.serviceCode == "expressDistributions" ||
      this.serviceCode == "stockBuyBackDistributions"
    );
  }

  get isPremierDistributionsService() {
    return this.serviceCode == "premierDistributions";
  }

  get isExpressDistributionsService() {
    return this.serviceCode == "expressDistributions";
  }

  get isStockBuyBackDistributionsService() {
    return this.serviceCode == "stockBuyBackDistributions";
  }

  get isAddressSearchService() {
    return this.serviceCode == "addressSearch";
  }

  get isVendorDistributionsService() {
    return this.serviceCode == "vendorDistributions";
  }

  get isRolloverService() {
    return (
      this.serviceCode == "expressAutomaticRollovers" ||
      this.serviceCode == "enhancedRollovers" ||
      this.serviceCode == "premierRollovers"
    );
  }

  get isUncashedChecksService() {
    return (
      this.serviceCode == "uncashedChecksQualified" ||
      this.serviceCode == "uncashedChecksNonQualified"
    );
  }

  get isUncashedChecksQualifiedService() {
    return this.serviceCode == "uncashedChecksQualified";
  }

  get isUncashedChecksNonQualifiedService() {
    return this.serviceCode == "uncashedChecksNonQualified";
  }

  get isRecurringBenefitService() {
    return this.serviceCode == "recurringBenefits";
  }

  get isEnhancedIraService() {
    return this.serviceCode == "enhancedIra";
  }

  get isNextLevelContributionService() {
    return this.serviceCode == "nextLevelContribution";
  }

  get serviceAllowsPaymentInstructionsToBeEntered() {
    return this.isDistributionsService;
  }

  get sumClientPaidFees() {
    return Math.addAmounts(
      this.totalRequestorFeesPaidBySubaccount,
      this.totalOtherFeesPaidByMasterAccount,
      this.totalOtherFeesPaidBySubaccount
    );
  }

  get sumMasterAccountPaidFees() {
    return this.totalOtherFeesPaidByMasterAccount;
  }

  get sumSubaccountPaidFees() {
    return Math.addAmounts(
      this.totalRequestorFeesPaidBySubaccount,
      this.totalOtherFeesPaidBySubaccount
    );
  }

  get requiresBankAccountVerification() {
    return (
      this.isPremierDistributionsService ||
      this.isExpressDistributionsService ||
      this.isUncashedChecksService ||
      this.isStockBuyBackDistributionsService ||
      this.isRecurringBenefitService
    );
  }

  buildRelatedObjects() {
    this._masterAccount = this._masterAccount || new Account();
    this._plan = null;
  }

  getFeeDependencyData(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/orders/" +
      this.id +
      "/fee-dependency-data";

    ApiRequest.send("GET", requestUrl).onComplete(
      function (response) {
        if (response.successful) {
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  static uploadParticipants(file, orderId, callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/orders/" +
      orderId +
      "/upload-participants";

    let formData = new FormData();
    formData.append("file", file);

    ApiRequest.upload(requestUrl, formData, OrderLine).onComplete(
      function (response) {
        if (response.successful) {
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response);
        }
      }.bind(this)
    );
  }

  checkUploadStatus(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/orders/" +
      this.id +
      "/upload-status";

    ApiRequest.send("GET", requestUrl).onComplete(
      function (response) {
        if (response.successful) {
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response);
        }
      }.bind(this)
    );
  }

  checkSubstatus(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/orders/" +
      this.id +
      "/substatus";

    ApiRequest.send("GET", requestUrl).onComplete(
      function (response) {
        if (response.successful) {
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response);
        }
      }.bind(this)
    );
  }

  addSupplementDocument(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/order/" +
      this.id +
      "/supplement-document";

    let formData = new FormData();
    formData.append("file", this.supplementDocument);

    // TECH-12312: send the selection this file is being saved against, not just the file.
    // saveOrder uploads before it PATCHes, so the server's persisted selection is still the
    // old one at this point -- and its guard refuses an order-level upload on a Targeted
    // order. Without this the user could never move an order from Targeted to Generic:
    // the upload was refused here, and saving the type first was refused for having no file.
    //
    // Omitted rather than sent as null when the user has opted out, since there is no
    // selection to claim and the server falls back to the persisted one.
    if (this.supplementDocumentType) {
      formData.append("supplementDocumentType", this.supplementDocumentType);
    }

    this._currentOperation = "saving";

    ApiRequest.upload(requestUrl, formData).onComplete(
      function (response) {
        this._currentOperation = null;
        if (response.successful) {
          this.supplementDocument = null;
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response);
        }
      }.bind(this)
    );
  }

  deleteSupplementDocument(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/order/" +
      this.id +
      "/supplement-document";

    ApiRequest.send("DELETE", requestUrl).onComplete(
      function (response) {
        if (response.successful) {
          this.supplementDocumentStoredFileId = null;
          this.supplementDocument = null;

          // TECH-12312: mirror the server's LOW-003 reconciliation in
          // OrdersApiController.DeleteOrderSupplementDocument, which also clears the selection
          // and the acknowledgement when the deleted file belonged to a `generic` opt-in
          // ("generic with no file" is a state the settings PATCH refuses outright).
          //
          // Without this the two ends drifted, and the drift was a review bypass, not a cosmetic
          // desync: the server cleared the type, the model did not, so supplementDocumentType was
          // never dirty and getDirtyAttributes left it out of the next PATCH. The order kept a
          // re-uploaded file with a null type, and Order.RequiresSupplementDocumentReview keys on
          // the type — so a delete-then-replace produced a client document that reached
          // participants without ever entering the Distributions review queue.
          //
          // Clearing it here makes the field dirty, so the next save re-sends the type the user
          // re-picks and the order gates normally.
          //
          // Only the `generic` case, matching the server's condition exactly: for
          // genericAndTargeted the server deliberately leaves the type alone, and clearing it
          // here would drift in the opposite direction.
          //
          // Deliberately NOT the _loadAttributes/_makeClean rehydration used by refresh/save/
          // delete on the base model. This runs mid-pipeline from PcEditOrderDetails.saveOrder,
          // before the PATCH, while the user's other unsaved edits are still dirty — rehydrating
          // would overwrite them with server values and _makeClean would drop them from the PATCH.
          if (this.supplementDocumentType === "generic") {
            this.supplementDocumentType = null;
            this.supplementDocumentAcknowledged = false;

            // ...and correct _original to match, because that is what dirtiness is measured
            // against. Clearing only the live values left _original still holding "generic", the
            // pre-delete server state, so deleting and then re-picking the same type made the two
            // agree again: getDirtyAttributes saw no change, omitted the field, and the server —
            // which cleared the type during the delete — kept null. That is this block's own
            // bypass reappearing one step further along, a replacement document carrying a null
            // type past Order.RequiresSupplementDocumentReview, which keys on the type.
            //
            // Writing the two fields directly is the narrow version of the _loadAttributes/
            // _makeClean rehydration rejected above: it re-syncs exactly the fields the server just
            // changed, and leaves every other unsaved edit dirty and bound for the PATCH.
            //
            // Not includeFieldIntoRequest: nothing ever calls clearFieldIntoRequest on this model,
            // so a force-include would persist for the page's lifetime and re-arm the
            // RequestBodyContainsKey guards on later saves that never touched the selection.
            // Correcting _original also keeps revertToOriginal honest — the server state really is
            // null now.
            this._original.supplementDocumentType = null;
            this._original.supplementDocumentAcknowledged = false;
          }

          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response);
        }
      }.bind(this)
    );
  }

  get isFetchingAssociateableOrderLines() {
    return this._isFetchingAssociateableOrderLines;
  }

  get associateableOrderLines() {
    return this._associateableOrderLines;
  }

  fetchOrderLinesThatCanBeAssociated(callback) {
    if (this._isFetchingAssociateableOrderLines) {
      if ($.isFunction(callback)) callback();

      return;
    }

    this._isFetchingAssociateableOrderLines = true;

    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/private/trust/orders/" +
      this.id +
      "/associatable-order-lines";

    ApiRequest.send("GET", requestUrl, null, OrderLine).onComplete(
      function (response) {
        this._isFetchingAssociateableOrderLines = false;

        if (response.successful) {
          this._associateableOrderLines = response.result;

          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  fetchOrderLinesForAssociation(callback) {
    if (this._isFetchingAssociateableOrderLines) {
      if ($.isFunction(callback)) callback();

      return;
    }

    this._isFetchingAssociateableOrderLines = true;

    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/private/trust/orders/" +
      this.id +
      "/order-lines-needing-association";

    ApiRequest.send("GET", requestUrl, null, OrderLine).onComplete(
      function (response) {
        this._isFetchingAssociateableOrderLines = false;

        if (response.successful) {
          this._associateableOrderLines = response.result;

          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  fetchOrderFundingSources(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/private/trust/orders/" +
      this.id +
      "/funding-sources";

    ApiRequest.send("GET", requestUrl).onComplete(
      function (response) {
        if (response.successful) {
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  createOrdersAndMoveOrderLines(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/private/orders/" +
      this.id +
      "/bulk-orders";

    ApiRequest.send("POST", requestUrl, null, Order).onComplete(
      function (response) {
        if (response.successful) {
          response.result.copyAttributesTo(this);
          this._makeClean();

          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  submitForProcessing(callback) {
    this._progressOrderStatus(callback, "submit");
  }

  submitForReview(callback) {
    this._progressOrderStatus(callback, "submit-for-approval");
  }

  cancel(callback) {
    this._progressOrderStatus(callback, "cancel");
  }

  reopen(callback) {
    this._progressOrderStatus(callback, "reopen");
  }

  approve(callback) {
    this._progressOrderStatus(callback, "approve");
  }

  _progressOrderStatus(callback, command) {
    var requestUrl = this.getApiPathForRefreshUpdateAndDelete() + "/" + command;

    ApiRequest.send("POST", requestUrl, null, Order).onComplete(
      function (response) {
        if (response.successful) {
          response.result.copyAttributesTo(this);
          this._makeClean();

          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  deleteAllOrderLines(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/orders/" +
      this.id +
      "/order-lines";

    ApiRequest.send("DELETE", requestUrl).onComplete(
      function (response) {
        this._isFetchingAssociateableOrderLines = false;

        if (response.successful) {
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  getRecurringPaymentOrderForCurrentMonth(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/recurring/plans/" +
      this.planId +
      "/current-month-payment-orders";

    ApiRequest.send("GET", requestUrl, null, null).onComplete((response) => {
      if (
        response.successful &&
        response.result &&
        response.result.length > 0
      ) {
        if ($.isFunction(callback)) {
          var recurringPaymentOrder = new Order(response.result[0].id);
          callback(recurringPaymentOrder, null);
        }
      } else {
        if ($.isFunction(callback)) callback(null, response.message);
      }
    });
  }

  getBulkOrderErrorNotificationInfo(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/bulk-orders/" +
      this.id +
      "/getBulkOrderErrorNotificationInfo";

    ApiRequest.send("GET", requestUrl).onComplete(
      function (response) {
        if (response.successful) {
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  sendBulkOrderErrorNotification(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/public/bulk-orders/" +
      this.id +
      "/sendBulkOrderErrorNotification";

    ApiRequest.send("GET", requestUrl).onComplete(
      function (response) {
        if (response.successful) {
          if ($.isFunction(callback)) callback(response.result, null);
        } else {
          if ($.isFunction(callback)) callback(null, response.message);
        }
      }.bind(this)
    );
  }

  static getSupplementDocumentReviewCount(callback) {
    var requestUrl =
      window.amplifyRuntimeConfig.get("VUE_APP_API_BASE_URL") +
      "/private/orders/supplement-document-review-count";

    ApiRequest.send("GET", requestUrl, null, Order).onComplete(
      function (response) {
        if (response.successful) {
          callback(response.result, null);
        } else {
          callback(null, response);
        }
      }.bind(this)
    );
  }
}

export default Order;
